import { DialogContent } from '@mui/material'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import FormTextField from 'components/atoms/FormTextField'

import useEntityData from 'hooks/useEntityData'

import {
  ROLE_ADMIN,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_PARTNER_ADMIN,
  ROLE_PARTNER_READ_ONLY,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  ROLE_SUPER_ADMIN,
} from '../../../auth'

const createStatuses = (t) => [
  { label: t('users.status.disabled'), value: 'disabled' },
  { label: t('users.status.enabled'), value: 'enabled' },
]
const createAccountTypes = (t) => [
  { label: t(`users.accountTypes.${ROLE_ADMIN}`), value: ROLE_ADMIN },
  {
    label: t(`users.accountTypes.${ROLE_PARTNER_ADMIN}`),
    value: ROLE_PARTNER_ADMIN,
  },
  {
    label: t(`users.accountTypes.${ROLE_SUPER_ADMIN}`),
    value: ROLE_SUPER_ADMIN,
  },
  { label: t(`users.accountTypes.${ROLE_READ_ONLY}`), value: ROLE_READ_ONLY },
  {
    label: t(`users.accountTypes.${ROLE_PARTNER_READ_ONLY}`),
    value: ROLE_PARTNER_READ_ONLY,
  },
  { label: t(`users.accountTypes.${ROLE_LOGISTICS}`), value: ROLE_LOGISTICS },
  { label: t(`users.accountTypes.${ROLE_B2C}`), value: ROLE_B2C },
  { label: t(`users.accountTypes.${ROLE_POST_SALES}`), value: ROLE_POST_SALES },
]

const UserInfoFields = ({ allowedRoles }) => {
  const { items } = useEntityData({
    entity: 'partners',
    newPage: 0,
    limit: 9999,
  })
  const { t } = useTranslation()

  const statuses = useMemo(() => createStatuses(t), [t])
  const accountTypes = useMemo(() => createAccountTypes(t), [t])

  const showPartner = useMemo(
    () =>
      allowedRoles.includes(ROLE_PARTNER_ADMIN) ||
      allowedRoles.includes(ROLE_PARTNER_READ_ONLY),
    [allowedRoles]
  )

  return (
    <>
      <DialogContent>
        <Row>
          <FormTextField
            name="firstName"
            label={t('users.userInfoDialog.name')}
            fullWidth
          />
          <FormTextField
            name="lastName"
            label={t('users.userInfoDialog.lastName')}
            fullWidth
          />
        </Row>

        <FormTextField
          name="email"
          label={t('users.userInfoDialog.email')}
          fullWidth
        />
      </DialogContent>

      <Divider />

      <DialogContent>
        <Row>
          {showPartner && (
            <FormTextField
              select
              name="partnerId"
              label={t('users.userInfoDialog.partner')}
              fullWidth
            >
              {items.map((option) => (
                <MenuItem key={option.partnerId} value={option.partnerId}>
                  {option.name}
                </MenuItem>
              ))}
            </FormTextField>
          )}
          <FormTextField
            select
            name="accountType"
            label={t('users.userInfoDialog.accountType')}
            fullWidth
          >
            {accountTypes
              .filter((item) => allowedRoles.includes(item.value))
              .map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </FormTextField>
          <FormTextField
            select
            name="status"
            label={t('users.userInfoDialog.status')}
            fullWidth
          >
            {statuses.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FormTextField>
        </Row>
      </DialogContent>

      <Divider />
    </>
  )
}

export default UserInfoFields

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& > div:first-of-type': {
    marginRight: theme.spacing(2),
  },
  '& > div:nth-of-type(3)': {
    marginLeft: theme.spacing(2),
  },
}))
